import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupsIcon from '@mui/icons-material/Groups';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import { Box, CircularProgress, Grid, Tab, Tabs, Typography } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContainerApp, SelectWrapperApp } from '../components/custom/CustomComponents';
import PlanificadorPlantilla from '../components/equipos/PlanificadorPlantilla';
import ValoracionScouting from '../components/equipos/ValoracionScouting';
import { Context } from '../service/Context';
import theme from '../styles/theme';
import { capitalizar, formatClubTeamName, ordenarPorCategoria } from '../utils/UtilesComun';

/**
 * Estilo visual estandarizado para los selectores de la cabecera (Equipo y Temporada),
 * alineado con el resto de la aplicación (BarraBuscarLiga, etc.).
 */
const ESTILO_SELECTOR_CABECERA = {
  backgroundColor: 'transparent',
  border: '1px solid #64748b',
  borderRadius: `${theme.borderRadius}px`,
  transition: 'border-color 0.15s ease-in-out, background-color 0.15s ease-in-out',
  height: 34,
  textAlign: 'left',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    '& .MuiSelect-icon': {
      color: theme.palette.primary.main,
    },
  },
  '&.Mui-focused': {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 0 0 1px rgba(15, 118, 110, 0.25)',
  },
  '& .MuiSelect-select': {
    py: 0.5,
    pl: 1.5,
    pr: '30px !important',
    fontSize: '0.88rem',
    fontWeight: 400,
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  '& .MuiSelect-icon': {
    color: theme.palette.text.secondary,
    fontSize: '1.2rem',
    right: 6,
    transition: 'color 0.15s ease-in-out',
  },
};

const MENU_PROPS_SELECTOR_CABECERA = {
  PaperProps: {
    sx: {
      borderRadius: `${theme.borderRadius}px`,
      border: `0.5px solid ${theme.palette.primary.main}`,
      boxShadow: '0 8px 24px -4px rgba(0, 0, 0, 0.12), 0 2px 6px -1px rgba(0, 0, 0, 0.04)',
      mt: 0.5,
      maxHeight: 320,
      '& .MuiMenuItem-root': {
        fontSize: '0.85rem',
        fontWeight: 500,
        py: 0.75,
        px: 1.5,
        borderRadius: '4px',
        mx: 0.5,
        my: 0.25,
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(15, 118, 110, 0.08)',
          color: theme.palette.primary.main,
          '& .MuiTypography-root': {
            color: theme.palette.primary.main,
          },
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(15, 118, 110, 0.12)',
          fontWeight: 700,
          color: theme.palette.primary.main,
          '& .MuiTypography-root': {
            color: theme.palette.primary.main,
            fontWeight: 700,
          },
          '&:hover': {
            backgroundColor: 'rgba(15, 118, 110, 0.18)',
          },
        },
      },
    },
  },
};

/**
 * Página de un equipo con selector de temporada y pestañas de Valoración, Estadísticas y Planificador.
 */
const Equipo = () => {
  const {
    obtenerDatosEquipo,
    obtenerHistoricoEquipo,
    obtenerEquiposClub,
    obtenerPlantilla,
    idClub,
    clubNombre,
    datosHomeCache,
    equiposCache,
    setEquipoCacheItem,
  } = useContext(Context);

  const { routeIdEquipo } = useParams();
  const navigate = useNavigate();

  const cachedEquipo = equiposCache?.[routeIdEquipo];
  const cachedLigaId =
    cachedEquipo?.historicoEquipo?.find((h) => h.temporada === cachedEquipo?.temporadaSeleccionada)?.idLiga ||
    cachedEquipo?.historicoEquipo?.[0]?.idLiga;

  const equipoEnHomeInicial =
    !cachedEquipo?.datosEquipo &&
    datosHomeCache?.equiposClub?.find((eq) => String(eq.idEquipo) === String(routeIdEquipo));

  const [datosEquipo, setDatosEquipo] = useState(cachedEquipo?.datosEquipo || equipoEnHomeInicial);
  const [historicoEquipo, setHistoricoEquipo] = useState(cachedEquipo?.historicoEquipo);
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState(
    cachedEquipo?.temporadaSeleccionada || cachedEquipo?.historicoEquipo?.[0]?.temporada || ''
  );
  const [plantilla, setPlantilla] = useState(
    cachedLigaId ? cachedEquipo?.temporadas?.[cachedLigaId]?.plantilla : undefined
  );

  const esEquipoPropioInicial = Boolean(
    idClub && datosHomeCache?.equiposClub?.some((eq) => String(eq.idEquipo) === String(routeIdEquipo))
  );

  const equiposClubIniciales =
    cachedEquipo?.equiposClub?.length > 0
      ? cachedEquipo.equiposClub
      : esEquipoPropioInicial && datosHomeCache?.equiposClub?.length > 0
        ? datosHomeCache.equiposClub
        : [];
  const [equiposClub, setEquiposClub] = useState(equiposClubIniciales);
  const [loading, setLoading] = useState(!cachedEquipo?.datosEquipo && !equipoEnHomeInicial);

  const [tabActiva, setTabActiva] = useState(0); // 0: Valoración (activa por defecto)

  // Sincronizar estado inmediatamente durante render al cambiar de equipo en la URL
  const [prevRouteId, setPrevRouteId] = useState(routeIdEquipo);
  if (prevRouteId !== routeIdEquipo) {
    setPrevRouteId(routeIdEquipo);
    const cached = equiposCache?.[routeIdEquipo];
    const esPropioEnHome = Boolean(
      idClub && datosHomeCache?.equiposClub?.some((eq) => String(eq.idEquipo) === String(routeIdEquipo))
    );

    if (cached?.datosEquipo) {
      setDatosEquipo(cached.datosEquipo);
      setHistoricoEquipo(cached.historicoEquipo);
      const temp = cached.temporadaSeleccionada || cached.historicoEquipo?.[0]?.temporada || '';
      setTemporadaSeleccionada(temp);
      if (cached.equiposClub?.length > 0) {
        setEquiposClub(cached.equiposClub);
      } else if (esPropioEnHome && datosHomeCache?.equiposClub?.length > 0) {
        setEquiposClub(datosHomeCache.equiposClub);
      } else {
        setEquiposClub([]);
      }
      const idLiga =
        cached.historicoEquipo?.find((h) => h.temporada === temp)?.idLiga || cached.historicoEquipo?.[0]?.idLiga;
      if (idLiga && cached.temporadas?.[idLiga]?.plantilla) {
        setPlantilla(cached.temporadas[idLiga].plantilla);
      } else {
        setPlantilla(undefined);
      }
      setLoading(false);
    } else {
      const fallbackEquipo = datosHomeCache?.equiposClub?.find((eq) => String(eq.idEquipo) === String(routeIdEquipo));
      setDatosEquipo(fallbackEquipo);
      setHistoricoEquipo(undefined);
      setPlantilla(undefined);
      setTemporadaSeleccionada('');
      setEquiposClub(esPropioEnHome && datosHomeCache?.equiposClub?.length > 0 ? datosHomeCache.equiposClub : []);
      setLoading(!fallbackEquipo);
    }
  }

  const esEquipoPropio = useMemo(() => {
    return Boolean(
      idClub &&
        (datosHomeCache?.equiposClub?.some((eq) => String(eq.idEquipo) === String(routeIdEquipo)) ||
          (datosEquipo?.idEquipo &&
            String(datosEquipo.idEquipo) === String(routeIdEquipo) &&
            datosEquipo?.idClub &&
            Number(idClub) === Number(datosEquipo.idClub)))
    );
  }, [idClub, datosHomeCache?.equiposClub, routeIdEquipo, datosEquipo?.idEquipo, datosEquipo?.idClub]);

  // Cargar datos base iniciales del equipo al cambiar de ruta
  useEffect(() => {
    let cancelada = false;
    setTabActiva(0);

    const cargar = async () => {
      const cached = equiposCache?.[routeIdEquipo];
      if (!cached?.datosEquipo) {
        setLoading(true);
      }
      try {
        const [datosEquipoRes, historicoEquipoRes] = await Promise.all([
          obtenerDatosEquipo(routeIdEquipo),
          obtenerHistoricoEquipo(routeIdEquipo),
        ]);

        if (cancelada) {
          return;
        }

        const temporadaInicial = historicoEquipoRes?.[0]?.temporada || '';
        setDatosEquipo(datosEquipoRes);
        setHistoricoEquipo(historicoEquipoRes);
        setTemporadaSeleccionada((prev) => (prev ? prev : temporadaInicial));

        const esPropioFinal = Boolean(
          idClub &&
            (datosHomeCache?.equiposClub?.some((eq) => String(eq.idEquipo) === String(routeIdEquipo)) ||
              (datosEquipoRes?.idClub && Number(idClub) === Number(datosEquipoRes.idClub)))
        );

        const equiposIniciales =
          cached?.equiposClub?.length > 0
            ? cached.equiposClub
            : esPropioFinal && datosHomeCache?.equiposClub?.length > 0
              ? datosHomeCache.equiposClub
              : [];

        if (equiposIniciales.length > 0) {
          setEquiposClub(equiposIniciales);
        }

        setEquipoCacheItem(routeIdEquipo, {
          datosEquipo: datosEquipoRes,
          historicoEquipo: historicoEquipoRes,
          equiposClub: equiposIniciales,
          temporadaSeleccionada: temporadaInicial,
        });
        setLoading(false);

        // Cargar equipos del club en segundo plano para no bloquear el renderizado ni generar waterfall
        if (datosEquipoRes?.idClub && (!cached?.equiposClub || cached.equiposClub.length === 0)) {
          obtenerEquiposClub(datosEquipoRes.idClub)
            .then((equiposClubRes) => {
              if (cancelada || !equiposClubRes || equiposClubRes.length === 0) {
                return;
              }
              setEquiposClub(equiposClubRes);
              setEquipoCacheItem(routeIdEquipo, {
                equiposClub: equiposClubRes,
              });
            })
            .catch((err) => {
              console.error('Error al cargar equipos del club en segundo plano:', err);
            });
        }
      } catch (err) {
        console.error('Error al cargar datos del equipo:', err);
        if (!cancelada) {
          setLoading(false);
        }
      }
    };

    cargar();

    return () => {
      cancelada = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIdEquipo]);

  // Lista de temporadas únicas disponibles en el histórico del equipo
  const temporadasDisponibles = useMemo(() => {
    if (!historicoEquipo) {
      return [];
    }
    const seasons = [];
    historicoEquipo.forEach((h) => {
      if (h.temporada && !seasons.includes(h.temporada)) {
        seasons.push(h.temporada);
      }
    });
    return seasons;
  }, [historicoEquipo]);

  // Obtener los datos de la liga correspondiente a la temporada seleccionada
  const datosLigaSeleccionada = useMemo(() => {
    if (!historicoEquipo || historicoEquipo.length === 0) {
      return undefined;
    }
    return historicoEquipo.find((h) => h.temporada === temporadaSeleccionada) || historicoEquipo[0];
  }, [historicoEquipo, temporadaSeleccionada]);

  const [loadingPlantilla, setLoadingPlantilla] = useState(false);

  // Cargar plantilla para el planificador de la temporada activa (solo equipos propios)
  useEffect(() => {
    let cancelada = false;
    const cargarPlantillaTemporada = async () => {
      // Para equipos rivales o ajenos no se usa el planificador de plantilla
      if (!esEquipoPropio || !routeIdEquipo || !temporadaSeleccionada) {
        return;
      }

      // Evitar llamadas con datos de un equipo anterior aún no sincronizados
      if (datosEquipo?.idEquipo && Number(datosEquipo.idEquipo) !== Number(routeIdEquipo)) {
        return;
      }

      const idLiga = datosLigaSeleccionada?.idLiga;
      if (!idLiga) {
        return;
      }

      const cachedTemporada = equiposCache?.[routeIdEquipo]?.temporadas?.[idLiga];
      if (cachedTemporada?.plantilla) {
        setPlantilla(cachedTemporada.plantilla);
        setLoadingPlantilla(false);
      } else {
        setLoadingPlantilla(true);
      }

      try {
        const plantillaRes = await obtenerPlantilla(routeIdEquipo, idLiga);
        if (cancelada) {
          return;
        }

        setPlantilla(plantillaRes);
        setLoadingPlantilla(false);

        setEquipoCacheItem(routeIdEquipo, {
          temporadas: {
            [idLiga]: {
              plantilla: plantillaRes,
            },
          },
        });
      } catch (err) {
        console.error('Error al cargar plantilla:', err);
        if (!cancelada) {
          setLoadingPlantilla(false);
        }
      }
    };

    cargarPlantillaTemporada();

    return () => {
      cancelada = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esEquipoPropio, routeIdEquipo, temporadaSeleccionada, datosLigaSeleccionada?.idLiga, datosEquipo?.idEquipo]);

  const handleTemporadaChange = (nuevaTemporada) => {
    setTemporadaSeleccionada(nuevaTemporada);
    setEquipoCacheItem(routeIdEquipo, { temporadaSeleccionada: nuevaTemporada });
  };

  // Guardar en almacenamiento el último equipo visitado si pertenece al club del usuario
  useEffect(() => {
    if (esEquipoPropio && routeIdEquipo) {
      localStorage.setItem('ultimoEquipoClub', String(routeIdEquipo));
    }
  }, [esEquipoPropio, routeIdEquipo]);

  const nombreClubAMostrar =
    datosEquipo?.nombreClub || (esEquipoPropio ? clubNombre || localStorage.getItem('nombreClub') || 'Mi Club' : '');

  const equiposOrdenados = useMemo(() => {
    return ordenarPorCategoria(equiposClub || []);
  }, [equiposClub]);

  const opcionesEquipos = useMemo(() => {
    if (equiposOrdenados.length > 0) {
      return equiposOrdenados;
    }
    if (datosEquipo) {
      return [
        {
          idEquipo: Number(routeIdEquipo),
          nombreEquipo: datosEquipo.nombreEquipo,
          categoria: datosEquipo.categoria,
        },
      ];
    }
    return [
      {
        idEquipo: Number(routeIdEquipo),
        nombreEquipo: '',
        categoria: '',
      },
    ];
  }, [equiposOrdenados, datosEquipo, routeIdEquipo]);

  return (
    <ContainerApp maxWidth={false} sx={{ my: 4 }}>
      {/* Cabecera Centrada en Una Sola Fila */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            flexWrap: 'wrap',
          }}
        >
          {/* Identidad del Club */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShieldOutlined sx={{ fontSize: '1.4rem', color: 'primary.main', opacity: 0.8 }} />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '1.15rem', sm: '1.35rem' },
                fontWeight: 500,
                color: 'text.primary',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
                m: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {capitalizar(nombreClubAMostrar)}
            </Typography>
          </Box>

          {/* Separador vertical sutil */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: '1px',
              height: 22,
              backgroundColor: '#cbd5e1',
              mx: 0.5,
            }}
          />

          {/* Selectores */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <SelectWrapperApp
              value={Number(routeIdEquipo)}
              onChange={(e) => navigate(`/equipo/${e.target.value}`)}
              variant="standard"
              disableUnderline
              options={opcionesEquipos}
              optionValue="idEquipo"
              renderLabel={(equipo) => formatClubTeamName(equipo.nombreEquipo, equipo.categoria)}
              sx={{
                ...ESTILO_SELECTOR_CABECERA,
                minWidth: 160,
                width: 'auto',
              }}
              MenuProps={MENU_PROPS_SELECTOR_CABECERA}
            />

            {temporadasDisponibles.length > 0 ? (
              <SelectWrapperApp
                value={temporadaSeleccionada}
                onChange={(e) => handleTemporadaChange(e.target.value)}
                variant="standard"
                disableUnderline
                options={temporadasDisponibles}
                renderLabel={(temp) => `${temp}`}
                sx={{
                  ...ESTILO_SELECTOR_CABECERA,
                  minWidth: 125,
                  width: 'auto',
                }}
                MenuProps={MENU_PROPS_SELECTOR_CABECERA}
              />
            ) : (
              <Box sx={{ minWidth: 125, height: 34, visibility: 'hidden' }} />
            )}
          </Box>
        </Box>

        {/* Pestañas para equipos propios (Valoración / Planificador) Centradas justo debajo */}
        {esEquipoPropio && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <Tabs
              value={tabActiva}
              onChange={(e, nuevoValor) => setTabActiva(nuevoValor)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  minHeight: 36,
                  py: 0.5,
                  px: 2,
                },
              }}
            >
              <Tab
                icon={<AssessmentIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />}
                iconPosition="start"
                label="Valoración"
              />
              <Tab
                icon={<GroupsIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />}
                iconPosition="start"
                label="Planificador"
              />
            </Tabs>
          </Box>
        )}
      </Box>

      {/* Contenido inferior de la página */}
      {loading && !datosEquipo ? (
        <Box sx={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} sx={{ color: 'primary.main' }} />
        </Box>
      ) : datosEquipo ? (
        <>
          {/* Vista de Valoración y Rendimiento: Pestaña 0 para Equipos Propios o Vista Única para Equipos Ajenos */}
          {(!esEquipoPropio || tabActiva === 0) && (
            <ValoracionScouting
              key={`${datosEquipo.idEquipo}-${temporadaSeleccionada}-${datosLigaSeleccionada?.idLiga}`}
              datosEquipo={datosEquipo}
              temporada={temporadaSeleccionada}
              idLiga={datosLigaSeleccionada?.idLiga}
              datosLiga={datosLigaSeleccionada}
              esEquipoPropio={esEquipoPropio}
            />
          )}

          {/* Vista para Equipos Propios - Pestaña 1: Planificador de Plantilla */}
          {esEquipoPropio && tabActiva === 1 && (
            <Grid container sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ mx: -1 }}>
                  {loadingPlantilla && (!plantilla || plantilla.length === 0) ? (
                    <Box
                      sx={{
                        minHeight: 250,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 6,
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: 'primary.main' }} />
                    </Box>
                  ) : (
                    <PlanificadorPlantilla
                      plantilla={plantilla || []}
                      idEquipo={datosEquipo.idEquipo}
                      datosEquipo={datosEquipo}
                      equiposClub={equiposClub}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </>
      ) : null}
    </ContainerApp>
  );
};

export default Equipo;
